import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import type {
  CodeReviewContent,
  CodeReviewUIAnswer,
  CodeReviewHiddenAnswer,
  ReviewComment,
} from "@/types/assessment";
import { getInitialLanguage } from "@/types/assessment";
import { FileTabs, LanguageSelector } from "../shared";
import { CodeViewer } from "./code-viewer";
import { CommentsPanel } from "./comments-panel";
import { CommentBadge } from "./comment-badge";

interface CodeReviewInputProps {
  content: CodeReviewContent;
  value: CodeReviewUIAnswer | null;
  onChange: (value: CodeReviewUIAnswer) => void;
  onLanguageSave?: (language: string) => void;
  disabled?: boolean;
  showResults?: boolean;
  hiddenAnswer?: CodeReviewHiddenAnswer;
  userDefaultLanguage?: string;
}

// Generate unique client-side IDs (fix 3.2: replace deprecated substr with slice)
function generateId(): string {
  return `comment-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function CodeReviewInput({
  content,
  value,
  onChange,
  onLanguageSave,
  disabled = false,
  showResults = false,
  hiddenAnswer,
  userDefaultLanguage,
}: CodeReviewInputProps) {
  // Get available languages
  const languages = Object.keys(content.snippets);

  // Initialize state
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    getInitialLanguage(languages, userDefaultLanguage)
  );
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [activeCommentLine, setActiveCommentLine] = useState<number | null>(
    null
  );
  const [editingComment, setEditingComment] = useState<ReviewComment | null>(
    null
  );

  // Get current snippets for selected language
  const currentSnippets = content.snippets[selectedLanguage] || [];
  const currentFile = currentSnippets[selectedFileIndex];

  // Get comments from value
  const allComments = value?.comments || [];

  // Filter comments for current file
  const fileComments = useMemo(
    () =>
      allComments.filter(
        (c) => !c.filename || c.filename === currentFile?.filename
      ),
    [allComments, currentFile?.filename]
  );

  // Comment counts per file (for tabs)
  const commentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const snippet of currentSnippets) {
      counts[snippet.filename] = allComments.filter(
        (c) => !c.filename || c.filename === snippet.filename
      ).length;
    }
    return counts;
  }, [currentSnippets, allComments]);

  // Handle language change with confirmation if comments would be lost (fix 2.10)
  const handleLanguageChange = useCallback(
    (lang: string) => {
      // Warn user if they have comments that will be lost
      if (allComments.length > 0) {
        const confirmed = window.confirm(
          "Switching languages will clear your comments. Continue?"
        );
        if (!confirmed) return;
      }

      setSelectedLanguage(lang);
      setSelectedFileIndex(0);
      setActiveCommentLine(null);
      setEditingComment(null);
      onChange({
        language: lang,
        comments: [], // Reset comments when changing language
      });
      // Save the language preference to the backend
      onLanguageSave?.(lang);
    },
    [allComments.length, onChange, onLanguageSave]
  );

  // Handle comment submission
  const handleCommentSubmit = useCallback(
    (commentData: Omit<ReviewComment, "id">) => {
      const newComment: ReviewComment = {
        ...commentData,
        id: editingComment?.id || generateId(),
        filename: currentFile?.filename,
      };

      let updatedComments: ReviewComment[];

      if (editingComment) {
        // Update existing comment
        updatedComments = allComments.map((c) =>
          c.id === editingComment.id ? newComment : c
        );
        setEditingComment(null);
      } else {
        // Add new comment
        updatedComments = [...allComments, newComment];
      }

      onChange({
        language: selectedLanguage,
        comments: updatedComments,
      });

      setActiveCommentLine(null);
    },
    [
      allComments,
      currentFile?.filename,
      editingComment,
      selectedLanguage,
      onChange,
    ]
  );

  // Handle comment edit
  const handleCommentEdit = useCallback((comment: ReviewComment) => {
    setEditingComment((prev) => (prev?.id === comment.id ? null : comment));
    setActiveCommentLine(null);
  }, []);

  // Handle comment delete
  const handleCommentDelete = useCallback(
    (commentId: string) => {
      const updatedComments = allComments.filter((c) => c.id !== commentId);
      onChange({
        language: selectedLanguage,
        comments: updatedComments,
      });
    },
    [allComments, selectedLanguage, onChange]
  );

  // Use ref to avoid onChange in effect dependencies (fix 2.7)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize value on mount if not set
  useEffect(() => {
    if (!value) {
      onChangeRef.current({
        language: selectedLanguage,
        comments: [],
      });
    }
  }, [value, selectedLanguage]);

  return (
    <div className="space-y-4">
      {/* Language selector */}
      {languages.length > 1 && (
        <LanguageSelector
          languages={languages}
          selected={selectedLanguage}
          onChange={handleLanguageChange}
          disabled={disabled}
        />
      )}

      {/* File tabs */}
      <FileTabs
        files={currentSnippets}
        selectedIndex={selectedFileIndex}
        onChange={setSelectedFileIndex}
        commentCounts={commentCounts}
        disabled={disabled}
      />

      {/* Code viewer with inline comments */}
      <CodeViewer
        code={currentFile?.code || ""}
        language={selectedLanguage}
        filename={currentFile?.filename}
        comments={fileComments}
        activeCommentLine={activeCommentLine}
        onLineClick={setActiveCommentLine}
        onCommentSubmit={handleCommentSubmit}
        onCommentEdit={handleCommentEdit}
        onCommentDelete={handleCommentDelete}
        editingComment={editingComment}
        disabled={disabled}
      />

      {/* Comments summary panel */}
      <CommentsPanel
        comments={allComments}
        onEdit={handleCommentEdit}
        onDelete={handleCommentDelete}
        disabled={disabled}
      />

      {/* Results panel (after submission) */}
      {showResults && hiddenAnswer && (
        <CodeReviewResultsPanel
          hiddenAnswer={hiddenAnswer}
          selectedLanguage={selectedLanguage}
        />
      )}
    </div>
  );
}

// Results panel shown after submission - displays expected issues for learning
function CodeReviewResultsPanel({
  hiddenAnswer,
  selectedLanguage,
}: {
  hiddenAnswer: CodeReviewHiddenAnswer;
  selectedLanguage: string;
}) {
  const expectedIssues = hiddenAnswer.expected_issues[selectedLanguage] || [];

  return (
    <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
      <h4 className="font-medium">Expected Issues</h4>
      <p className="text-sm text-muted-foreground">
        The following issues were expected to be identified in this code:
      </p>

      <div className="space-y-3">
        {expectedIssues.map((issue, idx) => (
          <div key={idx} className="p-3 rounded-lg border bg-card">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-mono text-sm">
                  L{issue.start_line}
                  {issue.end_line &&
                    issue.end_line !== issue.start_line &&
                    `-${issue.end_line}`}
                </span>
                {issue.category && (
                  <CommentBadge category={issue.category} size="sm" />
                )}
              </div>
              <p className="text-sm">{issue.description}</p>
              {issue.suggestion && (
                <p className="text-sm text-muted-foreground mt-1">
                  💡 {issue.suggestion}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
